import { cva } from "class-variance-authority";

export const skeletonTabItemCva = cva("tab-item flex items-center cursor-pointer select-none gap-2 transition-all duration-200 ease-in-out dark:text-gray-200", {
  variants: {
    tile: {
      true: "flex-col items-center justify-center text-center p-1 rounded-lg",
      false: "px-4 py-2.5",
    },
    orientation: {
      horizontal: "rounded-lg",
      vertical: "w-full",
    },
  },
  compoundVariants: [
    {
      tile: true,
      orientation: "horizontal",
      class: "min-w-[88px] px-4",
    },
    {
      tile: false,
      orientation: "vertical",
      class: "max-w-[200px]",
    },
  ],
});

export const tabItemCva = cva("tab-item flex items-center select-none gap-1 transition-all duration-200 ease-in-out dark:text-gray-200 outline-hidden", {
  variants: {
    tile: {
      true: "flex-col items-center justify-center text-center p-1",
      false: "px-4 py-2.5",
    },
    orientation: {
      horizontal: "rounded-lg",
      vertical: "w-full",
    },
    active: {
      true: "text-primary-600 dark:text-primary-400",
      false: null,
    },
    listPosition: {
      left: null,
      right: null,
    },
    error: {
      true: "text-red-600! dark:text-red-400! dark:bg-red-800!",
      false: null,
    },
    disabled: {
      true: "cursor-not-allowed text-gray-400 dark:text-gray-600",
      false: "cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700 focus:bg-gray-50 dark:focus:bg-dark-700",
    },
    rounded: {
      true: "rounded-lg",
      false: null,
    },
    tone: {
      default: null,
      danger: "text-red-600! dark:text-red-400!",
    },
  },
  compoundVariants: [
    {
      // Without a colour of its own an idle tab inherits whatever the page sets, which is plain black in places.
      active: false,
      disabled: false,
      class: "text-gray-800 dark:text-gray-200",
    },
    {
      tile: true,
      orientation: "horizontal",
      class: "min-w-[88px] px-4 h-16",
    },
    {
      tile: true,
      orientation: "vertical",
      class: "min-h-16",
    },
    {
      tile: false,
      orientation: "vertical",
      class: "max-w-[200px]",
    },
    {
      tile: true,
      orientation: "vertical",
      active: true,
      class: "dark:bg-dark-800 font-medium",
    },
    {
      tile: false,
      orientation: "vertical",
      active: true,
      listPosition: "left",
      class: "border-r border-primary-600 dark:border-primary-400",
    },
    {
      tile: false,
      orientation: "vertical",
      active: true,
      listPosition: "right",
      class: "border-l border-primary-600 dark:border-primary-400",
    },
    {
      rounded: true,
      tile: true,
      orientation: "vertical",
      class: "px-3 py-2",
    },
    {
      rounded: true,
      tile: true,
      orientation: "vertical",
      active: true,
      class: "bg-gray-100 dark:bg-dark-800 font-medium",
    },
    {
      tile: true,
      orientation: "vertical",
      disabled: false,
      class: "hover:bg-gray-50 dark:hover:bg-dark-700",
    },
  ],
  defaultVariants: {
    tone: "default",
  },
});

/**
 * The App Store app header bar (VTabs `appearance="header"`): 14px text with a 17px icon 6px apart, 10px by 12px
 * padding and rounded top corners. Idle tabs use the theme text colour; the active tab is green with a medium
 * weight and only the inkbar under it, so no state paints a background. Keyboard focus shows a soft inset ring;
 * a mouse click never does.
 */
export const headerTabItemCva = cva("tab-item flex items-center gap-1.5 px-3 py-2.5 rounded-t-lg select-none whitespace-nowrap text-sm transition-colors duration-200 ease-in-out outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/40", {
  variants: {
    active: {
      true: "font-medium text-primary-600 dark:text-primary-400",
      false: "font-normal",
    },
    error: {
      true: "text-red-600! dark:text-red-400!",
      false: null,
    },
    disabled: {
      true: "cursor-not-allowed text-gray-400 dark:text-gray-600",
      false: "cursor-pointer",
    },
    tone: {
      default: null,
      danger: "text-red-600! dark:text-red-400!",
    },
  },
  compoundVariants: [
    {
      active: false,
      disabled: false,
      class: "text-(--text) hover:text-primary-600 dark:hover:text-primary-400",
    },
  ],
  defaultVariants: {
    tone: "default",
  },
});

export const tabIconCva = cva("flex items-center justify-center shrink-0", {
  variants: {
    tile: {
      true: "w-8 h-8 p-1.5 rounded-lg bg-gray-100 dark:bg-dark-700",
      false: null,
    },
    active: {
      true: null,
      false: null,
    },
    error: {
      true: "text-red-600! dark:text-red-400!",
      false: null,
    },
    disabled: {
      true: null,
      false: null,
    },
    tone: {
      default: null,
      danger: null,
    },
  },
  compoundVariants: [
    {
      tile: true,
      error: false,
      disabled: false,
      active: true,
      tone: "default",
      class: "text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-800/40",
    },
    {
      tile: true,
      tone: "danger",
      active: false,
      class: "bg-red-50! dark:bg-red-500/10!",
    },
    {
      tile: true,
      tone: "danger",
      active: true,
      class: "bg-red-100! dark:bg-red-500/25!",
    },
    {
      tile: true,
      error: true,
      disabled: false,
      active: true,
      class: "bg-red-100 dark:bg-red-800/40",
    },
  ],
  defaultVariants: {
    tone: "default",
  },
});

export const tabTitleCva = cva("text-sm max-w-full", {
  variants: {
    tile: {
      true: "text-center w-full",
      false: "whitespace-nowrap leading-3 wrap-break-word",
    },
    orientation: {
      horizontal: null,
      vertical: null,
    },
  },
  compoundVariants: [
    {
      tile: true,
      orientation: "horizontal",
      class: "leading-3 wrap-break-word",
    },
    {
      tile: true,
      orientation: "vertical",
      // Never an arbitrary break inside a word, but a long word (e.g. "Standaardweergaven") may hyphenate at a syllable
      // to fit the fixed tile column; this needs the page language, which i18n sets on <html lang>.
      class: "leading-4 break-normal hyphens-auto",
    },
  ],
  defaultVariants: {
    orientation: "horizontal",
  },
});

export const tabCountCva = cva("ml-1 inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-xs leading-none font-medium tabular-nums", {
  variants: {
    active: {
      true: "bg-primary-100 text-primary-900 dark:bg-primary-800/40 dark:text-primary-300",
      false: "bg-gray-100 text-gray-700 dark:bg-dark-700 dark:text-gray-300",
    },
  },
  defaultVariants: {
    active: false,
  },
});
